import { Button, Card, Flex } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { CtaButton, CtaVariant, PopupContent, TextBlock } from '@promo-popup-contract';
import { POPUP_CARD_WIDTH_RANGE, POPUP_IMAGE_WIDTH_RANGE } from '@promo-popup-contract';
import './PromoPopup.scss';

const FONT_WEIGHT_CSS = { regular: 400, bold: 700, black: 900 } as const;

const textStyle = (block: TextBlock | CtaButton, color: string) => ({
  fontFamily: block.fontFamily,
  fontSize: `${block.fontSize}px`,
  fontWeight: FONT_WEIGHT_CSS[block.fontWeight],
  textAlign: block.align,
  color,
});

/**
 * TCX-917 increment — background, border colour and label colour for the CTA
 * are handed down as CSS custom properties, NOT plain inline properties like
 * `textStyle` above sets for headline/body. An inline `backgroundColor`/
 * `color`/`borderColor` always wins over any stylesheet rule for that same
 * element and property — `:hover` included — short of `!important`, so a
 * hover recolour can only ever repaint what's already expressed through the
 * stylesheet (`var(--promo-cta-bg, ...)` in PromoPopup.scss), not something
 * set directly inline.
 *
 * `cta.variant` defaults to 'solid': a record saved before this increment
 * carries no `variant`, `borderColor` or hover fields at all, and this
 * function then emits exactly `--promo-cta-bg` (from `fill`) and
 * `--promo-cta-label` (from `labelColor`) — the same two colours it has
 * always rendered with — and no hover vars, so PromoPopup.scss's hover
 * fallback chain resolves back to those same resting colours (no visible
 * change on hover, which is also today's behaviour).
 *
 * Border and fill are skipped for 'link' even when the record has hover
 * values for them: the render contract for 'link' is "no background, no
 * border" full stop, on hover included, not just at rest.
 */
const ctaVars = (cta: CtaButton): Record<string, string> => {
  const variant: CtaVariant = cta.variant ?? 'solid';
  const vars: Record<string, string> = { '--promo-cta-label': cta.labelColor };

  if (cta.hoverLabelColor) vars['--promo-cta-hover-label'] = cta.hoverLabelColor;

  if (variant === 'solid') vars['--promo-cta-bg'] = cta.fill;
  if (variant !== 'link' && cta.hoverFill) vars['--promo-cta-hover-bg'] = cta.hoverFill;

  if (variant !== 'link' && cta.borderColor) vars['--promo-cta-border'] = cta.borderColor;
  if (variant !== 'link' && cta.hoverBorderColor) {
    vars['--promo-cta-hover-border'] = cta.hoverBorderColor;
  }

  return vars;
};

/**
 * Whether a visible border should be drawn at rest. Width/style stay a plain
 * inline property (unlike colour, they never need to change on `:hover`), and
 * are only set at all when a border should actually show — 'outline' always
 * shows one, 'solid' only when the admin picked a `borderColor`, and 'link'
 * never does. Omitting the property entirely (rather than forcing it to 0)
 * for the "no border" cases leaves AntD's own default untouched, which is
 * exactly what a pre-increment record already rendered with.
 */
const ctaBorderStyle = (cta: CtaButton): Pick<React.CSSProperties, 'borderWidth' | 'borderStyle'> => {
  const variant: CtaVariant = cta.variant ?? 'solid';
  const showsBorder = variant === 'outline' || (variant === 'solid' && !!cta.borderColor);
  return showsBorder ? { borderWidth: 2, borderStyle: 'solid' } : {};
};

const ctaStyle = (cta: CtaButton): React.CSSProperties => {
  const variant: CtaVariant = cta.variant ?? 'solid';
  return {
    fontFamily: cta.fontFamily,
    fontSize: `${cta.fontSize}px`,
    fontWeight: FONT_WEIGHT_CSS[cta.fontWeight],
    textDecoration: variant === 'link' ? 'underline' : undefined,
    ...ctaBorderStyle(cta),
    ...ctaVars(cta),
  } as React.CSSProperties;
};

/**
 * TCX-917 bug fix — `align` used to be set as `textAlign` on the `<Button>`
 * itself, which changed nothing an admin could see: AntD's `.ant-btn` is
 * `display: inline-flex; justify-content: center` at rest, and the button is
 * sized to its own content besides, so there was never any spare width for
 * `text-align` to act on.
 *
 * What admins actually expect — and what the control implies — is that the
 * BUTTON moves within the card. `.promo-popup-content`'s children (headline,
 * body, this wrapper) are plain block-level elements with no `text-align` of
 * their own set in PromoPopup.scss, so `text-align` on this wrapper is free
 * to position the button (still `inline-flex`, so it behaves as an
 * inline-level box to its block-level parent) without touching the headline
 * or body — those already carry their own explicit `textAlign` inline style,
 * which wins over anything inherited from here.
 *
 * Skipped entirely when `fullWidth` is set: the button then fills this
 * wrapper's full width (via AntD's `block` prop, applied in the JSX below),
 * leaving no room for `text-align` to move it — the same reason the editor
 * hides the alignment control in that case (see CtaFields).
 */
const ctaAlignStyle = (cta: CtaButton): React.CSSProperties =>
  cta.fullWidth ? {} : { textAlign: cta.align };

/**
 * TCX-917 increment (card width) — the admin-set card width, handed down as a
 * CSS custom property rather than a plain inline `maxWidth`.
 *
 * It has to be a variable for the same reason the CTA colours are: an inline
 * `max-width` on `.promo-popup-container` would beat the `@media (max-width:
 * 767px)` rule in PromoPopup.scss that shrinks the pop-up to `calc(100% -
 * 16px)`, silently deleting the mobile layout that rule exists to keep. As a
 * variable, the media query's own `max-width` still wins inside its block.
 *
 * Exported because TWO places need the same value from the same record — the
 * live `.promo-popup-container` (PromoPopup) and the editor's `.popup-preview`
 * wrapper (PopupPreviewCard) — and the fallback for a record that carries no
 * `cardWidth` must be identical in both. One function, one fallback.
 */
export const cardWidthVars = (content: Pick<PopupContent, 'cardWidth'>): React.CSSProperties =>
  ({
    '--promo-card-width': `${content.cardWidth ?? POPUP_CARD_WIDTH_RANGE.default}px`,
  }) as React.CSSProperties;

/**
 * TCX-917 increment (image size) — the artwork's rendered size, as CSS custom
 * properties on its frame.
 *
 * The admin sets the WIDTH; the ratio comes from the uploaded file's own
 * dimensions, which the record already carries. That is the whole reason this
 * scales without distorting or cropping: the frame is always the shape of the
 * file inside it, so `object-fit` has nothing left to crop or letterbox.
 *
 * A record with no image yet carries `width: 0, height: 0` (see EMPTY_CONTENT
 * in the editor), and `aspect-ratio: 0 / 0` is invalid — it would collapse the
 * frame. The pre-increment 218:260 is used until a real file arrives, which is
 * the shape the empty frame has always had.
 */
const FALLBACK_IMAGE_ASPECT = '218 / 260';

export const imageFrameVars = (
  content: Pick<PopupContent, 'image' | 'imageWidth'>,
): React.CSSProperties => {
  const { width, height } = content.image;
  const usable = typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0;
  return {
    '--promo-image-width': `${content.imageWidth ?? POPUP_IMAGE_WIDTH_RANGE.default}px`,
    '--promo-image-aspect': usable ? `${width} / ${height}` : FALLBACK_IMAGE_ASPECT,
  } as React.CSSProperties;
};

interface Props {
  content: PopupContent;
  /** Already resolved by the caller — this component never picks a button. */
  cta: CtaButton;
  /**
   * TCX-917 increment — the second button, already resolved by the caller
   * against its OWN rules, same as `cta`. `undefined` means no secondary is
   * configured at all: exactly one button renders, unchanged from before
   * this increment.
   */
  secondaryCta?: CtaButton;
  /**
   * TCX-917 increment (auto-dismiss timer) — the fraction of the countdown
   * still remaining, `1` (just started) down to `0` (about to close).
   * `undefined` renders no progress line at all, which is both the
   * backward-compatible default (a record with no `autoDismissSeconds`) and
   * what a caller with no timer to show passes.
   *
   * PopupCard never runs the countdown itself — it only paints whatever
   * fraction it is handed. PromoPopup owns the interval, the pause-on-hover
   * state and the dismissal; PopupPreviewCard hands this a fixed value so the
   * admin can see the look of the bar without a countdown actually running
   * in the editor.
   */
  dismissProgress?: number;
  onCtaClick?: () => void;
  onSecondaryCtaClick?: () => void;
  onDismiss?: () => void;
}

/** A single CTA button, wrapped in its own alignment container — shared by
 * the primary and the secondary so the two render identically.
 *
 * TCX-917 increment — the wrapper carries its own `--link` modifier
 * (independently of `promo-popup-button--link` on the Button itself) so
 * PromoPopup.scss can tighten the gap ABOVE this button when it is the
 * second one in a two-button group, without that selector ending in the same
 * `.promo-popup-button--link {` text the button's own rule already uses
 * (which would make that anchor non-unique for the source-text tests that key
 * off it).
 */
const CtaButtonView: React.FC<{ cta: CtaButton; onClick?: () => void }> = ({ cta, onClick }) => {
  const variant: CtaVariant = cta.variant ?? 'solid';
  return (
    <div
      className={`promo-popup-cta-align${variant === 'link' ? ' promo-popup-cta-align--link' : ''}`}
      style={ctaAlignStyle(cta)}
    >
      <Button
        type="primary"
        block={!!cta.fullWidth}
        className={`promo-popup-button promo-popup-button--${variant}`}
        onClick={onClick}
        disabled={!onClick}
        style={ctaStyle(cta)}
      >
        {cta.label}
      </Button>
    </div>
  );
};

/**
 * TCX-917 — the pop-up's markup, owned in ONE place.
 *
 * Rendered by two callers that must not drift apart: PromoPopup (what users
 * see) and the admin editor's PopupPreviewCard. The ticket requires the preview
 * to match the real pop-up, and this stylesheet assumes a specific element
 * structure — a second copy of that structure would keep the CSS while being
 * free to diverge from it, so the preview would silently start lying the first
 * time someone adjusts the layout.
 *
 * It takes the CTA already resolved rather than resolving it: the admin is not
 * the audience, and evaluating conditions against the admin's own subscription
 * would show them a refusal button no consultant will ever see.
 *
 * Without `onDismiss` the close button is inert and hidden from screen readers
 * (the preview), and without `onCtaClick` the CTA is not interactive.
 */
export const PopupCard: React.FC<Props> = ({
  content,
  cta,
  secondaryCta,
  dismissProgress,
  onCtaClick,
  onSecondaryCtaClick,
  onDismiss,
}) => {
  // `position` is data on the SecondaryCta config, not on the resolved
  // button — read straight off `content` (same source every other geometry
  // decision here already comes from) rather than threaded through as a
  // second prop. Defaults to 'below' only in the unreachable case where a
  // caller passes `secondaryCta` with no matching `content.secondaryCta`.
  const position = content.secondaryCta?.position ?? 'below';

  return (
    <Card
      size="small"
      className="promo-popup-card"
      style={{ backgroundColor: content.backgroundColor }}
    >
      <Button
        type="text"
        icon={<CloseOutlined />}
        className="promo-popup-close"
        onClick={onDismiss}
        disabled={!onDismiss}
        aria-hidden={!onDismiss}
        aria-label={onDismiss ? 'Close promotional pop-up' : undefined}
      />
      <Flex className="promo-popup-flex">
        {/*
         * TCX-917 increment (card width) — the frame, not the image, is the
         * flex item. An <img> placed directly in this row contributes its own
         * intrinsic-ratio height to the row, so widening the card made the
         * artwork taller and the CARD taller with it, even when the copy had
         * not grown at all. The frame contributes no height of its own (its
         * only child is taken out of flow), so the row's height stays the
         * copy's height and the artwork fills whatever that turns out to be.
         *
         * Decoration: the headline and body already carry every word.
         */}
        <div className="promo-popup-image-frame" style={imageFrameVars(content)}>
          <img className="promo-popup-image" src={content.image.url} alt="" />
        </div>
        <div className="promo-popup-content">
          <div
            className="promo-popup-headline"
            style={textStyle(content.headline, content.headline.color)}
          >
            {content.headline.text}
          </div>
          <p className="promo-popup-body" style={textStyle(content.body, content.body.color)}>
            {content.body.text}
          </p>
          {/*
           * TCX-917 increment — one or two buttons. With no secondary this
           * renders byte-for-byte what shipped before (a single
           * `.promo-popup-cta-align`, no group wrapper needed around it) —
           * see CtaButtonView above, the one place either button renders.
           * With a secondary, both sit inside `.promo-popup-cta-group`,
           * whose modifier class (PromoPopup.scss) decides row vs stack; the
           * `beside` row wraps instead of overflowing this ~476px column,
           * the same class of bug already fixed once in this file for a long
           * single-button label.
           */}
          {secondaryCta ? (
            <div className={`promo-popup-cta-group promo-popup-cta-group--${position}`}>
              <CtaButtonView cta={cta} onClick={onCtaClick} />
              <CtaButtonView cta={secondaryCta} onClick={onSecondaryCtaClick} />
            </div>
          ) : (
            <CtaButtonView cta={cta} onClick={onCtaClick} />
          )}
        </div>
      </Flex>
      {/*
       * TCX-917 increment (auto-dismiss timer) — a thin bar along the
       * bottom edge of the card, absolutely positioned so it sits in the
       * card's own bottom padding rather than in the flex flow above: it
       * never pushes the image, text or the beside/below button row. Decorative
       * only (`aria-hidden`) — the countdown itself is not accessible
       * information a screen reader needs, since the X remains a full
       * substitute for closing the pop-up at any time.
       */}
      {typeof dismissProgress === 'number' && (
        <div className="promo-popup-progress" aria-hidden="true">
          <div
            className="promo-popup-progress__bar"
            style={{ width: `${Math.max(0, Math.min(1, dismissProgress)) * 100}%` }}
          />
        </div>
      )}
    </Card>
  );
};
